<script lang="ts" setup>
import type { OnboardingConfig, OnboardingState, OnboardingStepConfig } from '~/types/onboarding';

import CoreOnboardingFlow from '~/components/Core/Onboarding/CoreOnboardingFlow.vue';
import { useOnboardingCompletion } from '~/composables/Onboarding/useOnboardingCompletion';
import { usePersonalisedOnboarding } from '~/composables/usePersonalisedOnboarding';
import { EVENTS } from '~/constants/events';
import { ENTRY_TRACKING_QUERY_PARAM, parseEntryTrackingFromQuery } from '~/utils/unified/entryTracking';
import { SELECTED_ENTITY_QUERY_PARAMS } from '~/utils/unified/selectedEntity';
import { readSignupHandoffEmail } from '~/utils/unified/signupHandoff';
import { UTM_KEY_QUERY_PARAM } from '~/utils/unified/signupLink';
import { parseSocialReturnFromQuery } from '~/utils/unified/socialReturn';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const analytics = useAnalyticsService();

const { message, completeMessage, fetching, utmKey } = usePersonalisedOnboarding();
const { completeOnboarding } = useOnboardingCompletion();
const { getFlag } = useFeatureFlags();
const { data: aboutYouEnabled } = await useAsyncData('marketing-consent-signup-enabled', () => getFlag('marketing-consent-signup-enabled'));

const { email, code } = route.query;
const hasPrefilled = typeof email === 'string' && typeof code === 'string';
// Decoded before stripUntrustedQueryParams runs in onMounted.
const { entrySource, entryCta } = parseEntryTrackingFromQuery(route.query);

// Also captured before stripping — social_* params are not preserved either.
const socialReturn = parseSocialReturnFromQuery(route.query);
// The /login card verified this email and found no account; the new-role context is on the session.
const handoffEmail = readSignupHandoffEmail(route.query);

const resolveInitialState = (): Partial<OnboardingState> | undefined => {
  if (typeof email === 'string' && typeof code === 'string') {
    return { email: email.trim(), otpCode: code.trim() };
  }
  if (socialReturn) {
    return {
      email: socialReturn.email,
      firstName: socialReturn.firstName,
      lastName: socialReturn.lastName,
      isNewUserRole: true,
    };
  }
  if (handoffEmail) {
    return { email: handoffEmail, isNewUserRole: true };
  }
  return undefined;
};

const initialState = resolveInitialState();
// Social returns and card hand-offs arrive as new users with a verified email, so they resume at the
// first step a new account needs.
const newUserResumeStepId = aboutYouEnabled.value ? 'aboutYou' : 'terms';
const initialStepId = hasPrefilled ? 'otp' : socialReturn || handoffEmail ? newUserResumeStepId : undefined;

const PRESERVED_QUERY_PARAMS: readonly string[] = ['referralCode', 'redirect', UTM_KEY_QUERY_PARAM, ...SELECTED_ENTITY_QUERY_PARAMS];

// Signup re-reads the lane off the query at the terms step, long after the strip runs, so an
// entryTracking param that decodes has to survive it; one that doesn't is noise like any other.
const preservedQueryParams: readonly string[] = entrySource || entryCta
  ? [...PRESERVED_QUERY_PARAMS, ENTRY_TRACKING_QUERY_PARAM]
  : PRESERVED_QUERY_PARAMS;

const stripUntrustedQueryParams = async () => {
  const hasParamsToStrip = Object.keys(route.query).some(
    key => !preservedQueryParams.includes(key),
  );
  if (!hasParamsToStrip) {
    return;
  }

  const preservedQuery: Record<string, string> = {};
  for (const param of preservedQueryParams) {
    const value = route.query[param];
    if (typeof value === 'string') {
      preservedQuery[param] = value;
    }
  }
  await router.replace({ path: route.path, query: preservedQuery });
};

const startTime = Date.now();
const flowCompleted = ref(false);
const lastStepId = ref<string | null>(null);
const lastStepIndex = ref(0);
// Social returns and card hand-offs are always new users — existing users finish on the card or in the OAuth callback.
const isNewUser = ref<boolean | null>(socialReturn || handoffEmail ? true : null);
const isMatchedInvite = ref(false);

const userType = computed<'new' | 'existing' | 'unknown'>(() => {
  if (isNewUser.value === true) {
    return 'new';
  }
  if (isNewUser.value === false) {
    return 'existing';
  }
  return 'unknown';
});

const durationSeconds = () => Math.round((Date.now() - startTime) / 1000);

const handleStepChanged = ({ stepId, stepIndex, totalSteps }: { stepId: string; stepIndex: number; totalSteps: number }) => {
  lastStepId.value = stepId;
  lastStepIndex.value = stepIndex;
  analytics.track(EVENTS.UNIFIED_CAMPAIGN_ONBOARDING_STEP_VIEWED, {
    stepId,
    stepIndex,
    totalSteps,
    entrySource,
    entryCta,
    utmKey: utmKey ?? undefined,
  });
};

const handleOtpVerified = ({ isNewUser: isNew, isMatchedInvite: matched }: { isNewUser: boolean; isMatchedInvite: boolean }) => {
  isNewUser.value = isNew;
  isMatchedInvite.value = matched;
  if (!isNew) {
    analytics.track(EVENTS.UNIFIED_CAMPAIGN_ONBOARDING_EXISTING_USER_SIGNED_IN, {
      entrySource,
      entryCta,
      durationSeconds: durationSeconds(),
    });
  }
};

const handleFlowCompleted = () => {
  flowCompleted.value = true;
  analytics.track(EVENTS.UNIFIED_CAMPAIGN_ONBOARDING_COMPLETED, {
    userType: userType.value,
    durationSeconds: durationSeconds(),
    stepsCompleted: lastStepIndex.value + 1,
    entrySource,
    entryCta,
    utmKey: utmKey ?? undefined,
  });

  // Accounts created at the terms step only — mirrors /login's registration funnel so social signups
  // completing through onboarding don't read as abandoned registrations. A matched invite reports
  // its auto sign-up from the login step instead.
  if (isNewUser.value && !isMatchedInvite.value) {
    analytics.track(EVENTS.UNIFIED_AUTH_SIGNUP_COMPLETED, {
      method: socialReturn ? `social_${socialReturn.provider.toLowerCase()}` : 'email_otp',
      entrySource,
      entryCta,
    });
  }
};

// Leaving for /login to finish the sign-in, or for the school account page after a refused sign-up,
// is not an abandoned onboarding.
const handleHandedOff = () => {
  flowCompleted.value = true;
};

onMounted(async () => {
  await stripUntrustedQueryParams();
  analytics.track(EVENTS.LOGIN_PAGE_VIEWED);
  analytics.track(EVENTS.UNIFIED_CAMPAIGN_ONBOARDING_STARTED, {
    entrySource,
    entryCta,
    hasPrefilled,
    hasSocialReturn: !!socialReturn,
    hasPersonalisedMessage: !!message.value,
    hasUtmKeyOverride: !!utmKey,
    utmKey: utmKey ?? undefined,
  });

  if (socialReturn) {
    analytics.track(EVENTS.UNIFIED_AUTH_REGISTRATION_STARTED, {
      method: `social_${socialReturn.provider.toLowerCase()}`,
      entrySource,
      entryCta,
    });
  }
});

onBeforeUnmount(() => {
  if (flowCompleted.value) {
    return;
  }
  analytics.track(EVENTS.UNIFIED_CAMPAIGN_ONBOARDING_ABANDONED, {
    lastStepId: lastStepId.value,
    stepsCompleted: lastStepIndex.value,
    userType: userType.value,
    durationSeconds: durationSeconds(),
    entrySource,
    entryCta,
    utmKey: utmKey ?? undefined,
  });
});

const config = computed<OnboardingConfig>(() => {
  const steps: OnboardingStepConfig[] = [];

  steps.push({
    id: 'personalised',
    blinkyMessage: message.value ?? t('unified.onboarding.personalised'),
  });

  steps.push(
    {
      id: 'welcome',
      blinkyMessage: t('unified.onboarding.welcome'),
    },
    {
      id: 'email',
      blinkyMessage: t('unified.onboarding.email'),
      component: 'email',
    },
    {
      id: 'otp',
      blinkyMessage: t('unified.onboarding.otp'),
      component: 'otp',
    },
  );

  if (aboutYouEnabled.value) {
    steps.push(
      {
        id: 'aboutYou',
        blinkyMessage: t('unified.onboarding.aboutYou'),
        component: 'aboutYou',
        skipIf: state => !state.isNewUserRole,
      },
      {
        id: 'tooYoung',
        blinkyMessage: t('unified.onboarding.tooYoung'),
        component: 'tooYoung',
        skipIf: state => !state.tooYoung,
      },
    );
  }

  steps.push(
    {
      id: 'terms',
      blinkyMessage: t('unified.onboarding.termsAndConditions'),
      component: 'terms',
      // New accounts must accept the terms; existing roles skip straight past it.
      skipIf: state => !state.isNewUserRole || state.tooYoung,
    },
    {
      id: 'passwordSetup',
      blinkyMessage: t('unified.onboarding.passwordSetup'),
      component: 'passwordSetup',
      // Offered only when sign-up handed back a token (password login is on); a social account
      // already signs in through its provider.
      skipIf: state => !state.canSetPassword || !!socialReturn,
    },
    {
      id: 'complete',
      blinkyMessage: completeMessage.value,
      onComplete: completeOnboarding,
      skipIf: state => state.tooYoung,
    },
  );

  return {
    resetStepId: 'email',
    steps,
  };
});
</script>

<template>
  <div data-testid="onboarding-page" class="h-dvh">
    <KUICoreTransitionFadeIn>
      <KUICoreContainerCentered v-if="fetching" key="loading">
        <KUICoreLoaderAnyway />
      </KUICoreContainerCentered>

      <CoreOnboardingFlow
        v-else
        key="flow"
        :config="config"
        :initial-state="initialState"
        :initial-step-id="initialStepId"
        :entry-source="entrySource"
        :entry-cta="entryCta"
        @step-changed="handleStepChanged"
        @otp-verified="handleOtpVerified"
        @flow-completed="handleFlowCompleted"
        @handed-off="handleHandedOff"
      />
    </KUICoreTransitionFadeIn>
  </div>
</template>
